/**
 * HR-portal FTUE — severance-terms import wizard (`/severance-import`).
 *
 * Owns the four on-page coachmarks of the `portal-hr-activation` tour that walk
 * an HR admin through the severance-terms import wizard:
 *  - the drag-drop dropzone and the "Download Worksheet" button, both shown
 *    while the wizard is on its first step (`step === 'upload'`)
 *  - the term-changes review table and the "Set terms for N people" button,
 *    both shown once the wizard reaches its review step (`step === 'review'`)
 *
 * `<SeveranceEntitlementGate>` does not mount the wizard until the org's
 * severance entitlement is known and granted, so a mounted wizard already
 * implies entitlement — none of these steps re-check `features.severance`. (The
 * sidebar `nav-severance` step in `use-portal-hr-ftue` still gates on the flag,
 * because that link renders regardless of entitlement.)
 *
 * Mounted from `SeveranceImportWizard`, where `step` lives. The layout hook
 * (`use-portal-hr-ftue`) owns the flow start and the nav step that leads here;
 * this hook only adds the four follow-up steps (orders x2-x5) so each
 * `condition` can close over the wizard's live `step` state, matching the
 * job-seeker pattern (`use-opportunities-ftue`, `use-learn-ftue`) and the
 * People import wizard's `use-portal-import-ftue`.
 *
 * The dropzone and the review table are LARGE regions, so neither is
 * `dismissOnAction` — a stray click inside them must not complete the step
 * (mirrors `use-resume-upload-ftue`). The way forward for those two is the "Got
 * it" ack, or the re-eval effect below auto-advancing once the wizard moves past
 * the step's screen. The template-download button and the confirm button are
 * each one small button, so both ARE `dismissOnAction`: clicking them is the
 * admin acting on the intent (mirrors `internalJobsAdd`).
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const SEVERANCE_IMPORT_PATH = '/severance-import'

export interface PortalSeveranceFtueArgs {
  /** The import wizard is on its first step. The page passes `step === 'upload'`;
   *  only then are the dropzone and the template button rendered and those two
   *  steps a candidate. */
  uploadStep: boolean
  /** The import wizard is on its review step. The page passes `step === 'review'`;
   *  only then are the review table and the confirm button rendered and those
   *  two steps a candidate. */
  reviewStep: boolean
}

/** This hook's own step ids, read by the re-eval effect below so it only ever
 *  retires a step this hook registered, never one owned by another hook on the
 *  shared flow. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.severanceUpload,
  PORTAL_HR_STEP.severanceTemplate,
  PORTAL_HR_STEP.severanceReview,
  PORTAL_HR_STEP.severanceConfirm,
]

/**
 * Registers the four severance-import steps and keeps the tour in step with
 * the wizard's `step`. Call once from `SeveranceImportWizard`, where `step`
 * lives. Returns the anchor refs for the drag-drop `<div>`, the template
 * download button, the review table, and the confirm button.
 */
export function usePortalSeveranceFtue({ uploadStep, reviewStep }: PortalSeveranceFtueArgs) {
  const dropzoneAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceImportDropzone)
  const templateAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceTemplateCta)
  const reviewTableAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceReviewTable)
  const confirmAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.severanceConfirmCta)

  // Live wizard state for the step `condition`s (registered once below, so the
  // closures must read the current values rather than the mount-time snapshot).
  const uploadStepRef = useRef(uploadStep)
  uploadStepRef.current = uploadStep
  const reviewStepRef = useRef(reviewStep)
  reviewStepRef.current = reviewStep

  // Register the four steps once and re-pick the candidate (registration alone
  // does not trigger evaluation). Idempotent; joins the shared portal-hr tour.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.severanceUpload,
        phase: PORTAL_HR_STEP.severanceUpload,
        order: PORTAL_HR_ORDER.severanceUpload,
        trigger: { type: 'immediate' },
        // Only a candidate on this page while the wizard is on its upload step;
        // off-page / past-upload it is skipped (not marked seen) so it never
        // blocks the rest of the flow (see the note in `use-resume-upload-ftue`).
        condition: () => onPortalPath(SEVERANCE_IMPORT_PATH) && uploadStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceImportDropzone,
        placement: 'bottom',
        allowAnchorInteraction: true,
        // Large dropzone region: no `dismissOnAction` (a stray click must not
        // dismiss). Forward = the "Got it" ack, or an upload that advances the
        // wizard and trips the re-eval effect below.
        content: {
          body: 'Upload the severance selection terms worksheet for all affected employees. Grab the template on the right if you need it.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.severanceTemplate,
        phase: PORTAL_HR_STEP.severanceTemplate,
        order: PORTAL_HR_ORDER.severanceTemplate,
        trigger: { type: 'immediate' },
        // Same screen as the dropzone above -- the FormatGuide sidecar card (and
        // its "Download Worksheet" button) only renders on the wizard's first
        // step.
        condition: () => onPortalPath(SEVERANCE_IMPORT_PATH) && uploadStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceTemplateCta,
        placement: 'bottom',
        // One small button that starts a download (no nav): clicking it is
        // intent, so complete the step there rather than leaving the coachmark
        // hanging (mirrors `internalJobsAdd`).
        dismissOnAction: true,
        content: {
          body: "Download this worksheet first -- it's pre-filled with your current employees and the terms they carry today. Edit what's changing, then upload it back here.",
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.severanceReview,
        phase: PORTAL_HR_STEP.severanceReview,
        order: PORTAL_HR_ORDER.severanceReview,
        trigger: { type: 'immediate' },
        // Only a candidate once the wizard has a preview to show.
        condition: () => onPortalPath(SEVERANCE_IMPORT_PATH) && reviewStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceReviewTable,
        placement: 'bottom',
        // The review table is a large region: no `dismissOnAction`, same
        // reasoning as the dropzone above. Forward = the "Got it" ack, or the
        // re-eval effect once the admin confirms or backs out of review.
        content: {
          body: "Each changed term shows old vs new -- salary, lump sum, continuation, whatever you're updating.",
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.severanceConfirm,
        phase: PORTAL_HR_STEP.severanceConfirm,
        order: PORTAL_HR_ORDER.severanceConfirm,
        trigger: { type: 'immediate' },
        // Same screen as the review table above.
        condition: () => onPortalPath(SEVERANCE_IMPORT_PATH) && reviewStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.severanceConfirmCta,
        placement: 'bottom',
        // One small button that commits the import (no nav): clicking it is
        // intent, so complete the step there.
        dismissOnAction: true,
        content: {
          body: 'Nothing is saved until you click here.',
          dismissLabel: 'Got it',
        },
      }),
    ])

    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when the wizard step changes (a plain re-render does not re-run
  // the engine). If the active step is one of this hook's own four and its
  // `condition` has flipped false (the wizard moved to a different screen)
  // before the user acked it, mark it seen so the tour advances cleanly
  // instead of the coachmark vanishing mid-step. Otherwise just re-pick the
  // candidate. Scoped to this hook's own step ids so it never retires a step
  // owned by another hook on the shared flow. Mirrors `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: uploadStep/reviewStep intentionally re-trigger evaluation; the effect reads live state via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId)) {
      const step = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (step?.condition && !step.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [uploadStep, reviewStep])

  return {
    /** Spread onto the drag-drop dropzone `<div>` on the wizard's first step. */
    dropzoneAnchor,
    /** Attach to the "Download Worksheet (Current Employees)" `<Button>` inside `FormatGuide`. */
    templateAnchor,
    /** Spread onto the review/term-changes table's wrapping `<div>`. */
    reviewTableAnchor,
    /** Attach to the "Set terms for N people" `<Button>`. */
    confirmAnchor,
  }
}
